import { logger } from '../../utils/index.js';
import type { Wager } from '../../wager/Wager.js';

/**
 * Product item accepted by the self-service cart.
 * Kept structurally compatible with `ProductItem` in Nova Foundation so this package stays framework agnostic.
 */
export type SelfServiceProductItem = {
  headers: { source: string };
  isLive: boolean;
  payload: unknown[];
  wagers: unknown[];
  wagerString: null;
  productProperties: {
    addonProductName: string | undefined;
    cost: number;
    description: string;
    isQuickPick: boolean;
    productBrand: string;
    productFlag: string;
    productTitle: string;
  };
};

/**
 * Adds a single item to the self-service cart, provided by the self-service cart context.
 */
export type AddSelfServiceItem = (item: SelfServiceProductItem) => Promise<void>;

type SelfServiceWagerPaymentFlowParams = {
  wagers: Wager[];
  addItem: AddSelfServiceItem;
  regCloseTimes?: readonly (Date | string | undefined)[];
  brandLogos?: readonly (string | undefined)[];
};

type SelfServiceWagerPaymentFlowResult = {
  addedWagers: Wager[];
  failedWagers: Wager[];
};

const regCloseTimeFormatter = new Intl.DateTimeFormat('sv-SE', {
  weekday: 'long',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Stockholm',
});

function formatRegCloseTime(regCloseTime: Date | string | undefined): string | undefined {
  if (regCloseTime === undefined) {
    return undefined;
  }

  const date = regCloseTime instanceof Date ? regCloseTime : new Date(regCloseTime);

  if (Number.isNaN(date.getTime())) {
    // Mirrors the legacy behaviour where an unparsable value is passed through as-is.
    return typeof regCloseTime === 'string' ? regCloseTime : undefined;
  }

  return regCloseTimeFormatter.format(date);
}

/**
 * Adds wagers to the self-service cart instead of paying them.
 *
 * Uses the wager instances to build the information needed by the self-service cart.
 * A wager that fails to be added is reported back as failed, the remaining wagers are still processed.
 *
 * @param root0 params
 * @param root0.wagers wagers to add to the self-service cart
 * @param root0.addItem self-service cart `addItem` function, provided by the consuming app
 * @param root0.regCloseTimes registration close time per wager, matched by index, used for the cart description
 * @param root0.brandLogos brand logo per wager, matched by index, defaults to the lowercased product name
 * @returns the wagers that were added and the wagers that failed
 */
export async function paySelfServiceWagers({
  wagers,
  addItem,
  regCloseTimes = [],
  brandLogos = [],
}: SelfServiceWagerPaymentFlowParams): Promise<SelfServiceWagerPaymentFlowResult> {
  const addedWagers: Wager[] = [];
  const failedWagers: Wager[] = [];

  for (const [index, wager] of wagers.entries()) {
    try {
      const [productName] = wager.getProductsInBet();

      if (productName === undefined) {
        throw new Error('Wager does not contain a product bet');
      }

      const cost = wager.bets.reduce((sum, bet) => bet.price + sum, 0);
      const regCloseTime = formatRegCloseTime(regCloseTimes[index]);
      const itsWager = wager.compose();

      const productItem: SelfServiceProductItem = {
        headers: { source: 'SvS:LB:ITS' },
        isLive: false,
        payload: [itsWager],
        wagers: [itsWager],
        // The self-service cart discriminates between wager objects and wager strings on this field.
        // eslint-disable-next-line unicorn/no-null
        wagerString: null,
        productProperties: {
          addonProductName: undefined,
          cost,
          description: regCloseTime === undefined ? '' : `Spelstopp: ${regCloseTime}`,
          isQuickPick: wager.bets.some((bet) => bet.isQuickPick),
          productBrand: brandLogos[index] ?? productName.toLowerCase(),
          productFlag: productName,
          productTitle: productName,
        },
      };

      // eslint-disable-next-line no-await-in-loop
      await addItem(productItem);
      addedWagers.push(wager);
    } catch (error) {
      failedWagers.push(wager);
      logger.info('Failed adding wager to self-service cart', { error, wagerId: wager.localId });
    }
  }

  return { addedWagers, failedWagers };
}
